import { Test } from "./Test";
import { Condition, PartialConditionSchema } from "./Condition";
import { Answer } from "./Answer";
import tv, { SchemaType } from "typed-object-validator";

export type QuestionType = "multiple" | "open" | "number" | "location" | "title" | "multipletable" | "image" | "ensure";
export const QuestionTypeSchema = tv
    .value("multiple")
    .or(tv.value("open"))
    .or(tv.value("number"))
    .or(tv.value("location"))
    .or(tv.value("title"))
    .or(tv.value("multipletable"))
    .or(tv.value("ensure"))
    .or(tv.value("image"));

export const PartialQuestionSchema = tv.object({
    id: tv.number().optional(),
    parameter: tv.string().min(1, "Geef een parameternaam in.").max(30, "Deze parameternaam is te lang, max 30 karakters.").nullable(),
    question: tv.string().min(1, "Geef een vraag in.").max(1000, "Deze vraag is te lang."),
    type: QuestionTypeSchema,
    optional: tv.boolean(),
    optionsJson: tv.object().nullable(),
    order: tv.number(),
    conditions: tv.array(PartialConditionSchema),
});
export type PartialQuestion = SchemaType<typeof PartialQuestionSchema>;

export class Question<TOptions = MultipleQuestionOptions | NumberQuestionOptions | MultipleTableQuestionOptions> {
    id?: number;
    /**
     * @v-min 1 Geef een parameternaam in.
     * @v-max 30 Deze parameternaam is te lang, max 30 karakters.
     */
    parameter: string | null; // parameter is null when its answer is not tracked (for title)
    /**
     * @v-min 1 Geef een vraag in.
     * @v-max 1000 Deze vraag is te lang.
     */
    question: string;
    type: QuestionType;
    optional: boolean;
    optionsJson: TOptions | null;
    order: number;

    answers?: Answer[];
    conditions?: Condition[];

    constructor(
        conditions?: Condition[],
        optional: boolean = false,
        parameter: string | null = "",
        question: string = "",
        type: QuestionType = "open",
        optionsJson: TOptions | null = null,
        order: number = 0
    ) {
        this.optional = optional;
        this.parameter = parameter;
        this.question = question;
        this.type = type;
        this.conditions = conditions;
        this.optionsJson = optionsJson;
        this.order = order;
    }

    static clone(other: Question) {
        return new Question(other.conditions, other.optional, other.parameter, other.question, other.type, other.optionsJson, other.order);
    }
}

export type MultipleQuestionOptionsOption = string | { option: string; jumpToParameter?: string; unique?: boolean; inverted?: boolean };

export class MultipleQuestionOptions {
    options: MultipleQuestionOptionsOption[];
    other: boolean;
    multiple: boolean;
    random?: boolean;
    coded?: boolean;

    constructor(options: MultipleQuestionOptionsOption[] = [], multiple: boolean = false, other: boolean = false) {
        this.options = options;
        this.multiple = multiple;
        this.other = other;
    }
}

export class NumberQuestionOptions {
    min: number;
    max: number;

    constructor(min: number = Number.MIN_SAFE_INTEGER, max: number = Number.MAX_SAFE_INTEGER) {
        this.min = min;
        this.max = max;
    }
}

export class MultipleTableQuestionOptions {
    columns: string[];
    rows: string[];
    multiple: boolean;
    noneOption?: boolean;
    coded?: boolean;
    mirror?: boolean;

    constructor(columns: string[] = [], rows: string[] = [], multiple: boolean = false) {
        this.columns = columns;
        this.rows = rows;
        this.multiple = multiple;
    }
}

export const PartialQuestionnaireSchema = tv.object({
    id: tv.string().optional(),
    name: tv.string().min(1, "Geef je vragenlijst een naam.").max(100, "Deze naam is te lang."),
    questions: tv.array(PartialQuestionSchema),
    testId: tv.number().optional(),
    includeDuringRegistration: tv.boolean().optional(),
});
export type PartialQuestionnaire = SchemaType<typeof PartialQuestionnaireSchema>;

export interface Questionnaire {
    id: string;
    name: string;
    test?: Test;
    questions?: Question[];
    notifiedUsers: boolean;
    includeDuringRegistration: boolean;
}
